/**
 * Which filter controls a chart declares. A flag present and true means "this
 * chart is filtered by that": the header renders the control (see
 * ANALYTICS_CONTROLS) and the URL carries the param mapped to it (see
 * ANALYTICS_PARAM_FLAGS in services/filter-sync-configs.ts, which is where the
 * flag-to-param mapping is written down).
 *
 * Every flag is a plain `boolean`. Six of them used to be typed as the literal
 * `true`, which said nothing the optionality did not already say — a chart cannot
 * express "filtered by sites: no" by writing `sites: false` rather than leaving it
 * out, and no reader ever tried to.
 */
export interface IGraphFrontConfig {
  containers?: boolean;
  dateRange?: boolean;
  sites?: boolean;
  departureSites?: boolean;
  arrivalSites?: boolean;
  days?: boolean;
  maxContainers?: number;
  alertTypes?: boolean;
  containerCategories?: boolean;
  packaging?: boolean;
}

/**
 * Front Plotly config
 */
export interface IGraphLayout {
  title?: string;
  showlegend?: boolean;
  barmode?: string;
  xaxis?: {
    title: string;
    type: string;
    tickfont?: {
      size: number;
    };
    range?: number[];
    autorange?: true;
  };
  yaxis?: {
    title: string;
    tickfont?: {
      size: number;
    };
    hoverformat?: string;
    fixedrange?: boolean;
    rangemode?: string;
    autorange?: true;
  };
  legend?: {
    itemclick?: boolean;
    itemdoubleclick?: boolean;
  };
  autosize?: boolean;
  dragmode?: string | boolean;
  paper_bgcolor?: string;
  plot_bgcolor?: string;
  hovermode?: string;
  bargap?: number;
  width?: number;
  height?: number;
  margin?: {
    l?: number;
    r?: number;
    t?: number;
    b?: number;
    pad?: number;
  };
}

export interface IGraphConfig {
  displayModeBar?: boolean;
  responsive?: boolean;
  displaylogo?: boolean;
  scrollZoom?: boolean;
  doubleClick?: boolean;
  showAxisDragHandles?: boolean;
  modeBarButtonsToRemove?: string[];
}

/**
 * The per-request body fields a chart pins. One entry per name in `requests`,
 * read by index (analytics-controller.service.ts). Typed rather than `Object[]`,
 * which forced its only reader to take the whole chart config as `any`.
 */
export interface IGraphRequestParams {
  hasOnlyRed: boolean;
  xAxis: string;
}

export interface ITags {
  isHistorical?: boolean; // if false = Real Time & undefined = Neither
  isStellantis?: boolean; // if false = Supplier & undefined = Neither
  isStock?: boolean; // if false = Transit & undefined = Neither
}

/**
 * Interface Front graph configuration
 */
export interface IGraph {
  id: number;
  name: string;
  tags: ITags;
  requests: string[];
  requestsParams?: IGraphRequestParams[];
  frontConfig?: IGraphFrontConfig;
  layout: IGraphLayout;
  config: IGraphConfig;
}

/**
 * The filter sets a chart can declare. Seven charts declared a `frontConfig`
 * literal and only four distinct shapes came out of them, so the literals said
 * nothing about which charts are filtered alike — and adding a filter to "the
 * alert charts" meant finding them by reading all seven.
 *
 * They are shared objects, not templates to spread: nothing writes to a
 * `frontConfig` (the header and the URL sync only read flags off it), so two
 * charts naming the same shape are stating that they are filtered identically.
 */

/** Alert charts: how many alerts, per site, over the last N days. */
const SITE_ALERT_FILTERS: IGraphFrontConfig = {
  containerCategories: true,
  packaging: true,
  sites: true,
  days: true
};

/** {@link SITE_ALERT_FILTERS}, narrowable to one kind of alert. */
const TYPED_SITE_ALERT_FILTERS: IGraphFrontConfig = {
  alertTypes: true,
  ...SITE_ALERT_FILTERS
};

/** Stock charts: how many containers of which types, at a site, over a period. */
const SITE_PERIOD_FILTERS: IGraphFrontConfig = {
  containers: true,
  dateRange: true,
  sites: true
};

/** {@link SITE_PERIOD_FILTERS} for containers in transit: a route, not a site. */
const ROUTE_PERIOD_FILTERS: IGraphFrontConfig = {
  containers: true,
  dateRange: true,
  departureSites: true,
  arrivalSites: true
};

/**
 * Front graph configuration (graph type, plotly config, front filters config)
 */
export const graphsData: IGraph[] = [
  {
    id: 0,
    name: "redOrangePlant",
    tags: {
      isHistorical: false,
      isStellantis: true
    },
    requests: ["cacheGraphs"],
    requestsParams: [
      {
        hasOnlyRed: false,
        xAxis: "PLANT"
      }
    ],
    frontConfig: TYPED_SITE_ALERT_FILTERS,
    layout: {
      title: "",
      dragmode: false,
      barmode: "stack",
      xaxis: {
        title: "Stellantis Sites",
        type: "category"
      },
      yaxis: {
        title: "number of packaging (n)",
        rangemode: "tozero"
      }
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 1,
    name: "redOrangeSupplier",
    tags: {
      isHistorical: false,
      isStellantis: false
    },
    requests: ["cacheGraphs"],
    requestsParams: [
      {
        hasOnlyRed: false,
        xAxis: "SUPPLIER"
      }
    ],
    frontConfig: TYPED_SITE_ALERT_FILTERS,
    layout: {
      title: "",
      dragmode: false,
      barmode: "stack",
      xaxis: {
        title: "Suppliers Sites",
        type: "category"
      },
      yaxis: {
        title: "number of packaging (n)",
        rangemode: "tozero"
      }
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 2,
    name: "redPlant",
    tags: {
      isHistorical: false,
      isStellantis: true
    },
    requests: ["cacheGraphs"],
    requestsParams: [
      {
        hasOnlyRed: true,
        xAxis: "PLANT"
      }
    ],
    frontConfig: SITE_ALERT_FILTERS,
    layout: {
      title: "",
      dragmode: false,
      barmode: "bar",
      xaxis: {
        title: "Stellantis Sites",
        type: "category"
      },
      yaxis: {
        title: "number of packaging (n)",
        rangemode: "tozero"
      }
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 3,
    name: "redSupplier",
    tags: {
      isHistorical: false,
      isStellantis: false
    },
    requests: ["cacheGraphs"],
    requestsParams: [
      {
        hasOnlyRed: true,
        xAxis: "SUPPLIER"
      }
    ],
    frontConfig: SITE_ALERT_FILTERS,
    layout: {
      title: "",
      dragmode: false,
      barmode: "bar",
      xaxis: {
        title: "Suppliers Sites",
        type: "category"
      },
      yaxis: {
        title: "number of packaging (n)",
        rangemode: "tozero"
      }
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 4,
    name: "storage",
    tags: {
      isHistorical: true,
      isStellantis: undefined,
      isStock: true
    },
    requests: ["storage"],
    frontConfig: SITE_PERIOD_FILTERS,
    layout: {
      autosize: false,
      title: "",
      xaxis: {
        title: "Sites",
        type: "category",
        tickfont: { size: 11 },
        autorange: true
      },
      yaxis: {
        title: "Time (D)",
        tickfont: { size: 11 },
        hoverformat: ".1f",
        fixedrange: true,
        autorange: true
      },
      dragmode: false
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 5,
    name: "transit",
    tags: {
      isHistorical: true,
      isStellantis: undefined,
      isStock: false
    },
    requests: ["transit"],
    frontConfig: ROUTE_PERIOD_FILTERS,
    layout: {
      autosize: false,
      title: "",
      xaxis: {
        title: "Routes",
        type: "category",
        tickfont: { size: 11 },
        autorange: true
      },
      yaxis: {
        title: "Time (H)",
        tickfont: { size: 11 },
        hoverformat: ".1f",
        fixedrange: true,
        autorange: true
      },
      dragmode: false
    },
    config: {
      // displayModeBar: false
    }
  },
  {
    id: 6,
    name: "stockLevel",
    tags: {
      isHistorical: true,
      isStellantis: undefined,
      isStock: true
    },
    requests: ["stockLevel"],
    frontConfig: SITE_PERIOD_FILTERS,
    layout: {
      title: "",
      dragmode: false,
      xaxis: {
        title: "Time (D)",
        type: "date"
      },
      yaxis: {
        title: "number of packaging (n)",
        rangemode: "tozero"
      }
    },
    config: {
      // displayModeBar: false
    }
  }
];

//? Plotly Layout values can be found here :
// https://plotly.com/javascript/reference/layout/

export const defaultBarLayout: IGraphLayout = {
  title: "",
  hovermode: "closest",
  bargap: 0.6,
  paper_bgcolor: "rgba(0,0,0,0)",
  plot_bgcolor: "rgba(0,0,0,0)",
  margin: {
    l: 80,
    r: 20,
    t: 20,
    b: 50,
    pad: 0
  }
};

export const defaultLineLayout: IGraphLayout = {
  title: "",
  hovermode: "closest",
  legend: {
    itemclick: false,
    itemdoubleclick: false
  },
  paper_bgcolor: "rgba(0,0,0,0)",
  plot_bgcolor: "rgba(0,0,0,0)",
  margin: {
    l: 80,
    r: 20,
    t: 20,
    b: 50,
    pad: 0
  }
};

//? Plotly Config values can be found here :
// https://github.com/plotly/plotly.js/blob/master/src/plot_api/plot_config.js

export const defaultBarConfig: IGraphConfig = {
  displayModeBar: false,
  scrollZoom: false,
  doubleClick: false,
  showAxisDragHandles: false
};

export const defaultLineConfig: IGraphConfig = {
  displayModeBar: false,
  responsive: false,
  displaylogo: false,
  doubleClick: false
};
